<script setup>
import { computed } from "vue";

import InputError from "@/Components/Form/InputError.vue";

const emit = defineEmits(["update:modelValue", "change"]);

const props = defineProps({
  modelValue: {
    type: [Boolean, String, Number],
    required: false,
    default: false
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false
  },
  error: {
    type: String,
    required: false
  },
  size: {
    type: String,
    default: "md",
    validator: (value) => ["tiny", "xs", "sm", "md", "lg"].includes(value)
  },
  activeColor: {
    type: String,
    default: "primary",
    validator: (value) => ["primary", "secondary", "success", "warning", "error", "info"].includes(value)
  },
  inactiveColor: {
    type: String,
    default: "gray",
    validator: (value) => ["primary", "secondary", "success", "warning", "error", "info", "gray"].includes(value)
  },
  activeIcon: {
    type: String,
    default: "check"
  },
  inactiveIcon: {
    type: String,
    default: "close"
  }
});

const sizeClasses = computed(() => {
  switch (props.size) {
    case "tiny":
      return {
        button: "h-4 w-7",
        toggle: "h-3 w-3",
        translate: "translate-x-3",
        icon: "h-2 w-2"
      };
    case "xs":
      return {
        button: "h-5 w-9",
        toggle: "h-4 w-4",
        translate: "translate-x-4",
        icon: "h-2.5 w-2.5"
      };
    case "sm":
      return {
        button: "h-6 w-11",
        toggle: "h-5 w-5",
        translate: "translate-x-5",
        icon: "h-3 w-3"
      };
    case "md":
      return {
        button: "h-7 w-14",
        toggle: "h-6 w-6",
        translate: "translate-x-7",
        icon: "h-3.5 w-3.5"
      };
    default: // lg
      return {
        button: "h-9 w-16",
        toggle: "h-8 w-8",
        translate: "translate-x-7",
        icon: "h-4 w-4"
      };
  }
});

const colorClasses = computed(() => {
  const colors = {
    primary: "bg-primary",
    secondary: "bg-secondary",
    success: "bg-success",
    warning: "bg-warning",
    error: "bg-error",
    info: "bg-info",
    gray: "bg-gray-200"
  };

  return {
    active: colors[props.activeColor] || colors.primary,
    inactive: colors[props.inactiveColor] || colors.gray,
    ring: `focus:ring-${props.activeColor}`,
    icon: `text-${props.activeColor}`
  };
});

const model = computed({
  get() {
    return Boolean(props.modelValue);
  },
  set(value) {
    emit("update:modelValue", value);
    emit("change", value);
  }
});

// 키보드 접근성 추가
const handleKeyDown = (event) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    if (!props.disabled) {
      model.value = !model.value;
    }
  }
};

const getIconPath = (type) => {
  switch (type) {
    case "check":
      return "M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z";
    case "close":
    default:
      return "M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2";
  }
};
</script>

<template>
  <div>
    <div>
      <button
        type="button"
        role="switch"
        :aria-checked="model"
        :tabindex="disabled ? -1 : 0"
        :class="[
          'relative inline-flex flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent',
          'transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2',
          sizeClasses.button,
          colorClasses.ring,
          model ? colorClasses.active : colorClasses.inactive,
          disabled ? 'cursor-not-allowed opacity-50' : 'hover:shadow-md'
        ]"
        :disabled="disabled"
        @click="model = !model"
        @keydown="handleKeyDown"
      >
        <span class="sr-only">토글 스위치</span>

        <!-- 토글 버튼 -->
        <span
          :class="[
            'pointer-events-none relative inline-block transform rounded-full bg-white shadow ring-0',
            'transition duration-200 ease-in-out',
            sizeClasses.toggle,
            model ? sizeClasses.translate : 'translate-x-0'
          ]"
        >
          <!-- 비활성화 아이콘 -->
          <span
            :class="[
              'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity',
              model ? 'opacity-0 duration-100 ease-out' : 'opacity-100 duration-200 ease-in'
            ]"
            aria-hidden="true"
          >
            <svg :class="[sizeClasses.icon, 'text-gray-400']" fill="none" viewBox="0 0 12 12">
              <path :d="getIconPath(inactiveIcon)" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>

          <!-- 활성화 아이콘 -->
          <span
            :class="[
              'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity',
              model ? 'opacity-100 duration-200 ease-in' : 'opacity-0 duration-100 ease-out'
            ]"
            aria-hidden="true"
          >
            <svg :class="[sizeClasses.icon, colorClasses.icon]" fill="currentColor" viewBox="0 0 12 12">
              <path :d="getIconPath(activeIcon)" />
            </svg>
          </span>
        </span>
      </button>
    </div>

    <InputError v-if="error" :message="error" class="mt-1" />
  </div>
</template>
>
